import React from "react";
import { useWindow } from "@fluentui/react/lib/WindowProvider";
import { observer } from "owa-mobx-react";
import { useResizeObserver } from "owa-react-hooks/lib/useResizeObserver";
import { appModuleContainer, moduleContainer, bizChatInReadingPane, } from "./CopilotChatDrawerContainer.scss";
import { CopilotChatDrawer } from "owa-copilot-chat-drawer";
import { getDrawerPaneStackOrder } from "owa-drawer";
import { isCapabilityEnabled } from "owa-capabilities";
import { addinsDrawerPaneUnderRibbonCapability } from "owa-capabilities-definitions/lib/addinsDrawerPaneUnderRibbonCapability";
import { isBizchatSidepaneEnabled } from "owa-bizchat-shared/lib/utils/isBizchatSidepaneEnabled";
import { getCopilotUpsellEligibility } from "owa-copilot-upsell";
import { isCopilotChatDrawerShown, onCopilotChatDrawerAutoOpenPrerequisiteChanged, setAppCanvasWidthMutator, shouldDisableAutoFocusForCurrentOpen, shouldShowBizChatInReadingPane, } from "owa-copilot-chat-drawer-store";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { trace } from "owa-trace";
import classNames from "owa-classnames";
import { getCurrentModule } from "owa-app-module-store";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import { checkUserHeuristicForPreloading } from "owa-iframe/lib/hooks/useIframeSettings";
import { getUnifiedInboxResolvedMailboxInfoOrSelf } from "owa-session-store";

export interface CopilotChatDrawerContainerProps {
    children: React.ReactNode;
    containerStyles?: React.CSSProperties;
    alternatePanel?: React.ReactNode;
}

export const CopilotChatDrawerContainer: React.FunctionComponent<CopilotChatDrawerContainerProps> = observer(function CopilotChatDrawerContainer({ children, containerStyles, alternatePanel }) {
    const mailboxInfo = getModuleContextMailboxInfo(); // DrawerContainer is only rendered in the main window
    const effectiveMailboxInfo = getUnifiedInboxResolvedMailboxInfoOrSelf(mailboxInfo);
    const targetWindow = useWindow() ?? window;
    const isChatDrawerShown = isCopilotChatDrawerShown(targetWindow);
    const disableAutoFocusForCurrentOpen = shouldDisableAutoFocusForCurrentOpen();
    const bizchatWaitingForInteraction = React.useRef<boolean>(true);
    const renderChatDrawer = React.useRef<boolean>(false);
    // Lazily initialized on first render only
    const userHeuristicForPreloading = React.useRef<boolean | null>(null);
    if (userHeuristicForPreloading.current === null) {
        userHeuristicForPreloading.current = checkUserHeuristicForPreloading("BizChatSidebar");
    }
    
    // We don't want to close the Chat Drawer if the check is undefined (pending) for the sticky open scenario
    // so we only hide it when it's explicitly false.
    const isBizchatSidePaneEnabledValue = isBizchatSidepaneEnabled(undefined, undefined, true /* isSupportedByVoltron */);
    const isBizchatDisabled = isBizchatSidePaneEnabledValue === false;
    
    // Free/basic users have BizChat disabled; still permit the drawer so the upsell pane can render.
    const showCopilotUpsell = getCopilotUpsellEligibility(effectiveMailboxInfo);
    const canShowCopilotDrawer = !isBizchatDisabled || showCopilotUpsell;
    const showCopilotChatDrawer = isChatDrawerShown && canShowCopilotDrawer;
    
    if (isChatDrawerShown) {
        bizchatWaitingForInteraction.current = false;
    }
    
    const isBizchatPreloading = !showCopilotUpsell &&
        isGlobalFeatureEnabled("fwk-copilot-iframe-preload") &&
        bizchatWaitingForInteraction.current &&
        isBizchatSidePaneEnabledValue === true && // Don't start preloading until we know BizChat is enabled, to avoid unnecessary work for users who won't see the drawer.
        userHeuristicForPreloading.current === true; // Only mount the container if the heuristic comes back true
    
    const isBizchatCached = !showCopilotUpsell &&
        isGlobalFeatureEnabled("mon-copilot-bizchat-caching") &&
        !bizchatWaitingForInteraction.current;
    
    const shouldShowCopilotChatDrawer = (isBizchatPreloading || isBizchatCached || isChatDrawerShown) && canShowCopilotDrawer;
    
    if (!showCopilotUpsell &&
        shouldShowCopilotChatDrawer &&
        isGlobalFeatureEnabled("mon-copilot-bizchat-caching")) {
        // If ChatDrawer is rendered once, then we keep it rendered to preserve its caching state
        renderChatDrawer.current = true;
    }
    
    const isUnderRibbonEnabled = isCapabilityEnabled(addinsDrawerPaneUnderRibbonCapability);
    const copilotStackOrder = isUnderRibbonEnabled
        ? getDrawerPaneStackOrder("owa-app-pane-chatCopilot")
        : undefined;
    
    const copilotChatDrawerStyles = React.useMemo(() => {
        const drawerContainer = {
            ...(containerStyles ?? {}),
            ...(copilotStackOrder !== undefined ? { order: copilotStackOrder } : {}),
        };
        
        return {
            drawerContainer,
        };
    }, [containerStyles, copilotStackOrder]);
    trace.info(` Copilot chat drawer state (APP MODULE):
            shouldShowCopilotChatDrawer: ${shouldShowCopilotChatDrawer}
            bizchatWaitingForInteraction: ${bizchatWaitingForInteraction.current},
            isBizchatPreloading: ${isBizchatPreloading},
            isBizchatCached: ${isBizchatCached},
            isCopilotChatDrawerShown: ${isChatDrawerShown},
        `, "iframeWrapper");
    
    const appCanvasRef = React.useRef<HTMLDivElement>(null);
    
    // Update app canvas width when this container resizes
    const updateAppCanvasWidth = React.useCallback((rect: DOMRectReadOnly) => {
        setAppCanvasWidthMutator(targetWindow, rect.width);
        onCopilotChatDrawerAutoOpenPrerequisiteChanged(targetWindow);
    }, [targetWindow]);
    
    useResizeObserver("CopilotChatDrawerContainer", appCanvasRef, updateAppCanvasWidth);
    
    React.useEffect(() => {
        if (isBizchatSidePaneEnabledValue !== undefined) {
            onCopilotChatDrawerAutoOpenPrerequisiteChanged(targetWindow);
        }
    }, [isBizchatSidePaneEnabledValue, targetWindow]);
    
    const currentModule = getCurrentModule();
    
    const overrideScenario = React.useMemo(() => {
        return shouldShowCopilotChatDrawer &&
            isGlobalFeatureEnabled("cal-copilot-override-scenario") &&
            currentModule === "Calendar"
            ? "CalendarSidepane"
            : undefined;
    }, [shouldShowCopilotChatDrawer, currentModule]);
    
    return (<div ref={appCanvasRef} className={appModuleContainer}>
                <div className={classNames(moduleContainer, shouldShowBizChatInReadingPane(targetWindow) && bizChatInReadingPane)}>
                    {children}
                </div>
                {alternatePanel}
                {(shouldShowCopilotChatDrawer ||
            (!showCopilotUpsell && renderChatDrawer.current)) && (<CopilotChatDrawer mailboxInfo={effectiveMailboxInfo} targetWindow={targetWindow} isVisible={showCopilotChatDrawer} elementStyles={copilotChatDrawerStyles} overrideScenario={overrideScenario} disableAutoFocus={disableAutoFocusForCurrentOpen}/>)}
            </div>);
}, "CopilotChatDrawerContainer");
